// Trading section: API calls and pure helpers (labels, ticket building).
// Nothing here decides an order: the backend runs the guard rails again
// before anything leaves for Saxo.
import api from './api';
import { formatMoney, formatNumber } from '../theme/format';

export const ENV_LABELS = { sim: 'SIM', live: 'Réel' };

export const ORDER_TYPES = [
  { value: 'market', label: 'Au marché', hint: 'Exécuté tout de suite au meilleur prix disponible.' },
  { value: 'limit', label: 'Limite', hint: 'Exécuté seulement à ce cours ou mieux.' },
  { value: 'stop', label: 'Seuil', hint: 'Devient un ordre au marché quand le cours atteint le seuil.' },
  { value: 'stop_limit', label: 'Stop limite', hint: 'Au seuil, devient un ordre à cours limité.' },
  { value: 'trailing_stop', label: 'Stop suiveur', hint: 'Le seuil suit le cours à distance fixe, et ne recule jamais.' },
];

export const ORDER_TYPE_LABELS = Object.fromEntries(ORDER_TYPES.map((t) => [t.value, t.label]));

export const DURATIONS = [
  { value: 'day', label: 'Jour' },
  { value: 'gtc', label: 'Jusqu’à annulation' },
  { value: 'gtd', label: 'Jusqu’au…' },
];

export const STATUS_LABELS = {
  proposed: ['À confirmer', 'trade'],
  scheduled: ['Programmé', 'info'],
  placing: ['Envoi…', 'gold'],
  placed: ['Envoyé', 'gold'],
  working: ['En carnet', 'gold'],
  partial: ['Partiel', 'warn'],
  filled: ['Exécuté', 'gain'],
  cancelled: ['Annulé', 'neutral'],
  dismissed: ['Écarté', 'neutral'],
  expired: ['Expiré', 'neutral'],
  rejected: ['Refusé', 'loss'],
  failed: ['Échec', 'loss'],
  blocked: ['Bloqué', 'loss'],
};

export const ORIGIN_LABELS = {
  manual: 'Manuel',
  gladys: 'Gladys',
  pilot: 'Pilote du panel',
  rebalance: 'Rééquilibrage',
  deposit: 'Versement',
  dca: 'Achat étalé',
  trigger: 'Déclencheur',
  sniper: 'Sniper',
  mirror: 'Réinitialisation SIM',
};

export const RELATION_LABELS = { tp: 'Objectif', sl: 'Stop de protection', oco: 'OCO' };

export const OPEN_STATUSES = ['proposed', 'scheduled', 'placing', 'placed', 'working', 'partial'];

export const PROGRAM_STATUS = {
  active: ['En cours', 'gain'],
  paused: ['En pause', 'warn'],
  done: ['Terminé', 'neutral'],
  cancelled: ['Annulé', 'neutral'],
};

export const statusOf = (status) => STATUS_LABELS[status] || [status, 'neutral'];

// Accepts "12,5", "1 234.5"; NaN when not a number, null when empty.
export const parseNumber = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).replace(/[\s  ]/g, '').replace(',', '.');
  if (text === '') return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
};

export const EMPTY_FORM = {
  symbol: '', description: '', buy_sell: 'buy', amount: '', order_type: 'limit', price: '', stop_limit_price: '',
  trailing_distance: '', trailing_step: '', duration: 'day', gtd_date: '', take_profit: '', stop_loss: '',
  oco_enabled: false, oco_type: 'stop', oco_price: '', oco_stop_limit_price: '',
};

// buildTicket turns the form into the API's ticket; { error } when a field
// cannot be read (the backend validates the rest).
export const buildTicket = (form) => {
  const amount = parseNumber(form.amount);
  if (!form.symbol) return { error: 'Choisissez un instrument.' };
  if (amount === null || Number.isNaN(amount) || amount <= 0 || !Number.isInteger(amount)) return { error: 'Quantité : un nombre entier de titres.' };
  const ticket = { symbol: form.symbol, buy_sell: form.buy_sell, amount, order_type: form.order_type, duration: form.order_type === 'market' ? 'day' : form.duration };
  const read = (key, label, required) => {
    const v = parseNumber(form[key]);
    if (v === null) return required ? { error: `${label} manquant.` } : {};
    if (Number.isNaN(v) || v <= 0) return { error: `${label} invalide.` };
    return { value: v };
  };
  const fields = {
    limit: [['price', 'Cours limite', true]],
    stop: [['price', 'Seuil de déclenchement', true]],
    stop_limit: [['price', 'Seuil de déclenchement', true], ['stop_limit_price', 'Cours limite', true]],
    trailing_stop: [['price', 'Premier seuil', true], ['trailing_distance', 'Distance au marché', true], ['trailing_step', 'Pas', false]],
    market: [],
  }[form.order_type] || [];
  for (const [key, label, required] of fields) {
    const r = read(key, label, required);
    if (r.error) return r;
    if (r.value !== undefined) ticket[key] = r.value;
  }
  if (ticket.duration === 'gtd') {
    if (!form.gtd_date) return { error: 'Date d’échéance manquante.' };
    ticket.gtd_date = form.gtd_date;
  }
  if (form.buy_sell === 'buy' && ['market', 'limit'].includes(form.order_type) && !form.oco_enabled) {
    for (const [key, label] of [['take_profit', 'Objectif'], ['stop_loss', 'Stop de protection']]) {
      const r = read(key, label, false);
      if (r.error) return r;
      if (r.value !== undefined) ticket[key] = r.value;
    }
  }
  if (form.oco_enabled) {
    const price = read('oco_price', 'Cours du second ordre', true);
    if (price.error) return price;
    ticket.oco = { order_type: form.oco_type, price: price.value };
    if (form.oco_type === 'stop_limit') {
      const limit = read('oco_stop_limit_price', 'Cours limite du second ordre', true);
      if (limit.error) return limit;
      ticket.oco.stop_limit_price = limit.value;
    }
  }
  return { ticket };
};

// formFromTicket fills the form with a stored ticket (proposals).
export const formFromTicket = (t, description = '') => {
  const text = (v) => (v === null || v === undefined ? '' : String(v).replace('.', ','));
  return {
    ...EMPTY_FORM,
    symbol: t.symbol || '',
    description,
    buy_sell: t.buy_sell || 'buy',
    amount: text(t.amount),
    order_type: t.order_type || 'limit',
    price: text(t.price),
    stop_limit_price: text(t.stop_limit_price),
    trailing_distance: text(t.trailing_distance),
    trailing_step: text(t.trailing_step),
    duration: t.duration || 'day',
    gtd_date: t.gtd_date || '',
    take_profit: text(t.take_profit),
    stop_loss: text(t.stop_loss),
  };
};

// formFromQuery pre-fills the ticket from a link (Gladys, rebalancing,
// sniper): ?symbol=&side=&amount=&type=&price=&origin=&reason=, or opens a
// proposal to confirm (?proposal=<order id>).
export const formFromQuery = (search) => {
  const q = new URLSearchParams(search);
  const form = { ...EMPTY_FORM };
  if (q.get('symbol')) form.symbol = q.get('symbol');
  if (q.get('description')) form.description = q.get('description');
  if (['buy', 'sell'].includes(q.get('side'))) form.buy_sell = q.get('side');
  if (q.get('amount')) form.amount = q.get('amount');
  if (ORDER_TYPE_LABELS[q.get('type')]) form.order_type = q.get('type');
  if (q.get('price')) form.price = q.get('price').replace('.', ',');
  if (['day', 'gtc', 'gtd'].includes(q.get('duration'))) form.duration = q.get('duration');
  const proposal = Number(q.get('proposal')) || null;
  return { form, origin: q.get('origin') || 'manual', reason: q.get('reason') || '', proposal };
};

// ticketLine is the order in one French sentence.
export const ticketLine = (t) => {
  if (!t?.symbol) return '';
  const side = t.buy_sell === 'sell' ? 'Vente' : 'Achat';
  const type = (ORDER_TYPE_LABELS[t.order_type] || t.order_type || '').toLowerCase();
  const price = t.price ? ` à ${formatMoney(t.price)}` : '';
  return `${side} de ${formatNumber(t.amount)} ${t.symbol} — ${type}${price}`;
};

export const tradingApi = {
  status: () => api.get('/trading/status').then((r) => r.data),
  saveApp: (env, body) => api.put(`/trading/apps/${env}`, body).then((r) => r.data),
  deleteApp: (env) => api.delete(`/trading/apps/${env}`).then((r) => r.data),
  authUrl: (env) => api.get(`/trading/apps/${env}/auth`).then((r) => r.data.url),
  disconnect: (env) => api.delete(`/trading/apps/${env}/session`).then((r) => r.data),
  accounts: (env) => api.get(`/trading/apps/${env}/accounts`).then((r) => r.data.accounts || []),
  selectAccount: (env, accountKey) => api.put(`/trading/apps/${env}/account`, { account_key: accountKey }).then((r) => r.data),
  setEnv: (env, confirmation = '') => api.put('/trading/env', { env, confirmation }).then((r) => r.data),
  saveLimits: (limits) => api.put('/trading/limits', limits).then((r) => r.data),
  pause: (reason, cancelOpen) => api.post('/trading/pause', { reason, cancel_open: cancelOpen }).then((r) => r.data),
  resume: () => api.delete('/trading/pause').then((r) => r.data),
  preview: (ticket) => api.post('/trading/orders/preview', { ticket }).then((r) => r.data),
  place: (body) => api.post('/trading/orders', body).then((r) => r.data),
  orders: (params = {}) => api.get('/trading/orders', { params }).then((r) => r.data.orders || []),
  cancel: (id) => api.delete(`/trading/orders/${id}`).then((r) => r.data),
  modify: (id, body) => api.patch(`/trading/orders/${id}`, body).then((r) => r.data),
  positions: () => api.get('/trading/positions').then((r) => r.data.positions || []),
  order: (id) => api.get(`/trading/orders/${id}`).then((r) => r.data),
  confirm: (id, body) => api.post(`/trading/orders/${id}/confirm`, body).then((r) => r.data),
  previewBasket: (tickets) => api.post('/trading/baskets/preview', { tickets }).then((r) => r.data),
  placeBasket: (body) => api.post('/trading/baskets', body).then((r) => r.data.orders || []),
  automation: () => api.get('/trading/automation').then((r) => r.data),
  saveAutomation: (body) => api.put('/trading/automation', body).then((r) => r.data),
  programs: () => api.get('/trading/programs').then((r) => r.data.programs || []),
  createProgram: (body) => api.post('/trading/programs', body).then((r) => r.data),
  setProgramStatus: (id, status) => api.put(`/trading/programs/${id}`, { status }).then((r) => r.data),
  pilot: () => api.get('/trading/pilot').then((r) => r.data),
  savePilot: (body) => api.put('/trading/pilot', body).then((r) => r.data),
  simDivergence: () => api.get('/trading/sim/divergence').then((r) => r.data),
  // The SIM copy of the real portfolio: one buy per second at Saxo.
  mirrorSim: () => api.post('/trading/sim/mirror', null, { timeout: 290000 }).then((r) => r.data),
  // The autonomous pilot waits for its model (up to 3 minutes).
  replayPilot: () => api.post('/trading/pilot/run', null, { timeout: 290000 }).then((r) => r.data),
  pilotBacktest: () => api.get('/trading/pilot/backtest').then((r) => r.data),
};

export const PILOT_MODES = [
  { value: 'off', label: 'Désactivé', description: 'Le pilote ne lit pas les débats.' },
  { value: 'propose', label: 'Propositions', description: 'Il prépare les ordres, vous confirmez chacun dans la salle des ordres.' },
  { value: 'delayed', label: 'Envoi différé', description: 'Il les envoie à l’heure choisie, après un délai pendant lequel vous pouvez annuler.' },
  { value: 'auto', label: 'Envoi automatique', description: 'Il les envoie à l’heure choisie, sans attendre. Le coupe-circuit arrête tout.' },
  { value: 'agent', label: 'Autonome (agent IA)', description: 'Le rapporteur lit le rapport, le débat et tout le compte, puis décide seul des ordres du jour : quoi, combien, type, stops, étalement, heure. Envoi sans confirmation, chaque ordre contrôlé par le code.' },
];

// Modes that send orders without a confirmation per order.
export const PILOT_UNATTENDED = ['delayed', 'auto', 'agent'];

export const PILOT_CONVICTIONS = [
  { value: 'unanimous', label: 'Unanimité', hint: 'Seulement les décisions prises par tout le panel.' },
  { value: 'strong', label: 'Deux tiers', hint: 'Unanimité ou au moins deux tiers du panel (pondérés si les votes le sont).' },
  { value: 'majority', label: 'Majorité', hint: 'Toute décision majoritaire ; jamais une décision par défaut.' },
];

export const PILOT_DEBATES = [
  { value: 'own', label: 'Son propre débat', hint: 'Le jour du rééquilibrage, le panel débat du portefeuille du pilote et lui fixe son propre plan : il reste pertinent même quand le pilote s’éloigne de votre portefeuille. Un débat de plus par rééquilibrage, payé par les clés des experts.' },
  { value: 'shared', label: 'Le débat de votre portefeuille', hint: 'Le pilote lit seulement les débats de votre portefeuille réel, sur son propre compte : moins coûteux, mais ses lignes à lui ne sont débattues que comme « à garder ou non ».' },
];

export const PILOT_CADENCES = [
  { value: 'weekly', label: 'Hebdomadaire', hint: 'Le rééquilibrage a lieu le premier jour de bourse de la semaine ; les autres jours, seules les ventes totales et les renforcements unanimes (et les idées du jour) partent.' },
  { value: 'daily', label: 'Quotidien', hint: 'Chaque débat peut déclencher des ordres : plus de frais et d’allers-retours.' },
];

// The autonomy the panel's scored record earns the pilot (budget factor).
export const PILOT_TRUST = {
  probation: { label: 'Période d’essai', tone: 'warn' },
  trusted: { label: 'Confiance', tone: 'gain' },
  restricted: { label: 'Restreint', tone: 'loss' },
};

export const PANEL_DECISIONS = { renforcer: 'Renforcer', conserver: 'Conserver', alleger: 'Alléger', solder: 'Solder', reinvestir: 'Réinvestir la trésorerie', high: 'Intérêt fort', medium: 'Intérêt moyen', low: 'Intérêt faible', none: 'Aucun intérêt' };
export const PANEL_STATUSES = { consensus: 'unanimité', converged: 'unanimité après débat', majority: 'majorité', persistent: 'sans majorité', open: 'ouvert' };

// basketTickets turns suggested lines ({symbol, side, shares, price}) into
// tickets: a limit at the last price ± offsetBps (marketable, but capped),
// or market orders. Lines without shares are left out.
export const basketTickets = (lines, { kind = 'limit', offsetBps = 30 } = {}) => lines
  .filter((l) => l.shares > 0 && (l.side === 'buy' || l.side === 'sell'))
  .map((l) => {
    const ticket = { symbol: l.symbol, buy_sell: l.side, amount: l.shares, order_type: kind === 'market' ? 'market' : 'limit', duration: 'day' };
    if (kind !== 'market') {
      const f = l.side === 'buy' ? 1 + offsetBps / 10000 : 1 - offsetBps / 10000;
      ticket.price = Math.round(l.price * f * 100) / 100;
    }
    return ticket;
  })
  // Sells first: their proceeds pay for the buys.
  .sort((a, b) => (a.buy_sell === b.buy_sell ? 0 : a.buy_sell === 'sell' ? -1 : 1));

export const errorOf = (err, fallback) => err?.response?.data?.error || fallback;
